/**
 * ProjectInfoPanel — Upload-remediation directive (2026-09-14).
 *
 * Shows the project name/number/location best-effort extracted from
 * THIS batch's own cover/drawing-index sheet, with source evidence
 * (page number + literal matched text on that page), compared against
 * the project's already-entered fields. Purely a reviewer aid — the
 * backend never auto-applies anything; "Apply" here is an explicit
 * human action that PATCHes the Project document.
 *
 * Renders nothing when no cover/drawing-index page in this batch
 * yielded any labeled field (never fabricates a suggestion).
 */
import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, Check, FileSearch } from "lucide-react";
import { api } from "@/lib/api";

const FIELD_LABELS = { name: "Project name", number: "Project number", location: "Location" };

const AGREEMENT_TONE = {
  match: { color: "#10b981", label: "Matches project" },
  conflict: { color: "#f59e0b", label: "Conflicts with project" },
  no_existing_value: { color: "#3b82f6", label: "Not set on project yet" },
  not_extracted: { color: "#6b7d8a", label: "Not found on cover sheet" },
};

function FieldRow({ field, entry, projectId, onApplied }) {
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const agreement = entry?.agreement || "not_extracted";
  const tone = AGREEMENT_TONE[agreement] || AGREEMENT_TONE.not_extracted;
  if (agreement === "not_extracted") {
    return (
      <div className="flex items-center justify-between py-1.5" data-testid={`project-info-row-${field}`}>
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)]">{FIELD_LABELS[field]}</span>
        <span className="text-[11px] text-[var(--fop-text-4)]">Not found on cover sheet</span>
      </div>
    );
  }
  const canApply = (agreement === "conflict" || agreement === "no_existing_value") && !applied;
  const handleApply = async () => {
    setApplying(true);
    try {
      await api.patch(`/projects/${projectId}`, { [field]: entry.value });
      setApplied(true);
      onApplied?.(field, entry.value);
    } finally { setApplying(false); }
  };
  return (
    <div className="py-1.5 border-t border-[var(--fop-border)]/50 first:border-t-0" data-testid={`project-info-row-${field}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] shrink-0">{FIELD_LABELS[field]}</span>
        <span
          className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-[1px] rounded-sm"
          style={{ color: tone.color, border: `1px solid ${tone.color}55`, background: `${tone.color}0c` }}
          data-testid={`project-info-agreement-${field}`}
        >
          {tone.label}
        </span>
      </div>
      <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-[var(--fop-text)]" data-testid={`project-info-value-${field}`}>
          &ldquo;{entry.value}&rdquo;
          <span className="ml-2 text-[10px] font-mono text-[var(--fop-text-4)]">
            page {entry.source_page} · matched &ldquo;{entry.matched_label}&rdquo;
          </span>
        </div>
        {entry.existing_value && (
          <div className="text-[10px] font-mono text-[var(--fop-text-4)]" data-testid={`project-info-existing-${field}`}>
            currently: &ldquo;{entry.existing_value}&rdquo;
          </div>
        )}
        {canApply && (
          <button
            type="button"
            onClick={handleApply}
            disabled={applying}
            className="btn btn-ghost inline-flex items-center gap-1 text-[10px] disabled:opacity-50"
            data-testid={`project-info-apply-${field}`}
            title="Apply this extracted value to the project record"
          >
            <Check size={10} /> {applying ? "Applying…" : "Apply to project"}
          </button>
        )}
        {applied && (
          <span className="text-[10px] font-mono uppercase text-emerald-400" data-testid={`project-info-applied-${field}`}>
            Applied
          </span>
        )}
      </div>
      <div className="mt-0.5 text-[10px] font-mono text-[var(--fop-text-4)] italic" data-testid={`project-info-evidence-${field}`}>
        Source: &ldquo;{entry.evidence_text}&rdquo;
      </div>
    </div>
  );
}

export default function ProjectInfoPanel({ batchId, projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/upload_batches/${batchId}/project_info`);
      setData(d);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [batchId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!data || !data.extracted) return null;

  const conflictCount = Object.values(data.comparison || {}).filter((e) => e.agreement === "conflict").length;

  return (
    <div
      className="mb-4 border border-[var(--fop-border)] rounded-md p-3 bg-[var(--fop-surface-2)]"
      data-testid="project-info-panel"
      data-conflict-count={conflictCount}
    >
      <div className="flex items-center gap-2 mb-1">
        <FileSearch size={13} className="text-[var(--fop-text-3)]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-steel)]">
          Project info found on cover sheet
        </span>
        {conflictCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-400" data-testid="project-info-conflict-flag">
            <AlertTriangle size={10} /> {conflictCount} conflict{conflictCount === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {["name", "number", "location"].map((field) => (
        <FieldRow
          key={field}
          field={field}
          entry={data.comparison?.[field]}
          projectId={projectId}
          onApplied={load}
        />
      ))}
    </div>
  );
}
